import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { BudgetTypes, budgetTypePropType } from '@types/budget-types'
import { Card, CardHeading, CardContent } from '@components/layout/Card'

import { EditedMonthlyRetirementBudget } from './EditedMonthlyRetirementBudget'
import { MonthlyRetirementBudgetBucket } from './MonthlyRetirementBudgetBucket'

const MonthlyRetirementBudget = ({
  baseScenario,
  comparedScenario,
  budgetType,
  filter,
  ...rest
}) => {
  const passThroughProps = { style: rest.style, className: rest.className }
  const isComparison = !!comparedScenario
  const cardWrapperClasses = classNames(
    'flex flex-col',
    'ui-card--retirement-budget', {
      'ui-card--edited-retirement-budget': isComparison
    }
  )

  // Calculate shared computed props between <NormalBudget> and <ComparisonBudget>
  const baseScenarioRetirementIncomeDifference = baseScenario.actualRetirementIncome - baseScenario.desiredRetirementIncome
  const baseScenarioRetirementIncomeAbsoluteDifference = Math.abs(
    baseScenarioRetirementIncomeDifference
  )
  const baseScenarioRetirementIncomeDifferenceText = renderDifferenceText(
    baseScenarioRetirementIncomeDifference,
    { textPrefix: 'Current' }
  )
  const maxSize = Math.max(
    baseScenario.desiredRetirementIncome,
    baseScenario.actualRetirementIncome,
    (comparedScenario && comparedScenario.actualRetirementIncome) || 0
  )

  return (
    <Card {...passThroughProps} section className={cardWrapperClasses}>
      {isComparison ? (
        <ComparisonBudget {...{
          baseScenario,
          comparedScenario,
          baseScenarioRetirementIncomeAbsoluteDifference,
          baseScenarioRetirementIncomeDifferenceText,
          maxSize,
          filter
        }}/>
      ) : (
        <NormalBudget {...{
          budgetType,
          baseScenario,
          baseScenarioRetirementIncomeAbsoluteDifference,
          baseScenarioRetirementIncomeDifferenceText,
          maxSize,
          filter
        }}/>
      )}
    </Card>
  )
}

MonthlyRetirementBudget.propTypes = {
  /** Pass through prop applied to the root element */
  className: PropTypes.string,
  /** Pass through prop applied to the root element */
  style: PropTypes.object,
  /** Corresponds to Budget.selected enum where 0 = 'simple' and 1 = 'detailed' */
  budgetType: budgetTypePropType,

  /** Object describing the base scenario's desired retirement income as per the
   * client's retirement budget, and the actual retirement income as per the
   * maximum safe retirement income draw on the base scenario */
  baseScenario: PropTypes.exact({
    desiredRetirementIncome: PropTypes.number.isRequired,
    actualRetirementIncome: PropTypes.number.isRequired,
  }).isRequired,

  /** Object describing the compared scenario's desired retirement income as per
   * the client's retirement budget, and the actual retirement income as per the
   * maximum safe retirement income draw on the compared scenario */
  comparedScenario: PropTypes.exact({
    desiredRetirementIncome: PropTypes.number.isRequired,
    actualRetirementIncome: PropTypes.number.isRequired,

  }),
  filter: PropTypes.func
}

MonthlyRetirementBudget.defaultProps = {
  budgetType: BudgetTypes.simple
}

export { MonthlyRetirementBudget }

const NormalBudget = ({
  budgetType,
  baseScenario,
  baseScenarioRetirementIncomeAbsoluteDifference,
  maxSize,
  filter
}) => {
  const baseScenarioRetirementIncomeDifferenceText = renderDifferenceText(
    baseScenario.actualRetirementIncome -
      baseScenario.desiredRetirementIncome,
    { textPrefix: 'Current', textOnly: true }
  )
  const budgetTypeLabel = budgetType === BudgetTypes.simple ? 'Simple budget' : 'Detailed budget'
  const subtitle = (
    <span>Your <b>{budgetTypeLabel}</b> is currently being used.</span>
  )

  return (
    <Fragment>
      <CardHeading className='flex'>
        <div className='flex-[2] flex flex-col gap-1'>
          <h5>Monthly retirement budget</h5>
          {subtitle}
        </div>
        <div className='flex-1'>
          {filter ? filter() : null}
        </div>
      </CardHeading>
      <CardContent>
        <div className='budget-buckets'>
          <MonthlyRetirementBudgetBucket
            income={baseScenario.desiredRetirementIncome}
            maxSize={maxSize}
            headingText={'Desired retirement budget'}
            additionalClass={'bucket__container--required'} />
          <MonthlyRetirementBudgetBucket
            income={baseScenario.actualRetirementIncome}
            maxSize={maxSize}
            headingText={'Projected retirement income'}
            additionalClass={'bucket__container--projected'} />
          <MonthlyRetirementBudgetBucket
            income={baseScenarioRetirementIncomeAbsoluteDifference}
            maxSize={maxSize}
            headingText={baseScenarioRetirementIncomeDifferenceText}
            additionalClass={'bucket__container--difference'} />
        </div>
      </CardContent>
    </Fragment>
  )
}

const ComparisonBudget = ({
  baseScenario,
  comparedScenario,
  baseScenarioRetirementIncomeAbsoluteDifference,
  baseScenarioRetirementIncomeDifferenceText,
  maxSize,
  filter
}) => {
  const comparedScenarioRetirementIncomeDifference = comparedScenario.actualRetirementIncome - comparedScenario.desiredRetirementIncome
  const comparedScenarioRetirementIncomeAbsoluteDifference = Math.abs(
    comparedScenarioRetirementIncomeDifference
  )
  const comparedScenarioRetirementIncomeDifferenceText = renderDifferenceText(
    comparedScenarioRetirementIncomeDifference,
    { textPrefix: 'Current' }
  )

  return (
    <EditedMonthlyRetirementBudget
      baseScenarioDesiredRetirementIncome={baseScenario.desiredRetirementIncome}
      baseScenarioActualRetirementIncome={baseScenario.actualRetirementIncome}
      baseScenarioRetirementIncomeAbsoluteDifference={
        baseScenarioRetirementIncomeAbsoluteDifference
      }
      baseScenarioRetirementIncomeDifferenceText={
        baseScenarioRetirementIncomeDifferenceText
      }

      comparedScenarioDesiredRetirementIncome={
        comparedScenario.desiredRetirementIncome
      }
      comparedScenarioActualRetirementIncome={
        comparedScenario.actualRetirementIncome
      }
      comparedScenarioRetirementIncomeAbsoluteDifference={
        comparedScenarioRetirementIncomeAbsoluteDifference
      }
      comparedScenarioRetirementIncomeDifferenceText={
        comparedScenarioRetirementIncomeDifferenceText
      }

      maxSize={maxSize}
      filter={filter}
    />
  )
}

const renderDifferenceText = (difference, { textPrefix = '', textOnly = false }) => {
  const textSuffix = difference === 0 ? '' : (difference < 0 ? 'Shortfall' : 'Excess')

  if (textOnly) {
    return `${textPrefix} Income ${textSuffix}`
  } else {
    return <span><span className='bold'>{textPrefix} </span>Income {textSuffix}</span>
  }
}
